// src/pages/crypto-topup.ts —— USDT 收付「充值订单 / Top-up orders」页签（2026-10-16 生产案例：运营查不到订单号）。
// 由 crypto.ts 以一行挂接（文案并入 I18N_CRYPTO、视图追加在现有页签之后）；本文件不 import crypto.ts（避免加载环）。
import { fmtDateTime } from '../../../shared/src/time';
import type { AdminCtx } from '../domains/types';
import { str } from '../domains/types';
import { openDrawer } from '../domains/table';
import { facetText, statusText } from './crypto.monitor';
import { defineView, type ErasedView } from './types';
import { cell, chip, type CellVals, type ChipKind } from './ui';

/** GET /api/admin/topup-orders 一行（服务端 cryptopay.TopupOrderRow）。金额为 DECIMAL 文本原样。 */
export type TopupOrderRow = {
  id: number; user_id: number; uid: string; email_masked: string | null; telegram_username: string | null;
  network: string; source: string; address_mode: 'legacy' | 'shared' | string;
  amount_expected: string; base_amount: string | null; channel_fee: string; quote_gross: string | null; quote_fee: string | null; quote_net: string | null;
  address: string; registry_id: number | null; status: string; created_at: string; expires_at: string;
  deposit_id: number | null; deposit_tx_hash: string | null; deposit_amount: string | null; deposit_status: string | null; payment_event_id: number | null;
};

export const I18N_TOPUP = {
  zh: {
    cpTopupOrders: '充值订单', toOrderNo: '订单号', toUser: '用户', toDue: '应付 USDT', toBase: '充值本金', toChannelFee: '通道费', toQuoteGross: '报价充值额', toQuoteFee: '报价手续费',
    toQuoteNet: '报价到账额', toAddress: '收款地址', toMode: '地址模式', toMode_shared: '共享收款地址', toMode_legacy: '旧专属地址', toSource: '下单渠道', toSource_bot: 'Bot', toSource_web: '网页',
    toCreated: '创建时间', toExpires: '过期时间', toDepositHash: '到账交易哈希', toDeposit: '入账记录', toDepositNone: '未入账', toEvent: '链上流水', toDetail: '充值订单',
    toRules: '查询 Bot / 网页充值订单（只读）。关键词可填 UID（U100005）、#订单号、应付金额（如 100.001）、收款地址、到账交易哈希或邮箱前缀；按 UID 精确或金额区间筛选可用接口参数 uid、amount_min、amount_max。订单仅是收款线索，入账以链上核验为准；漏付 / 多付等异常到「异常流水」处理。',
  },
  en: {
    cpTopupOrders: 'Top-up orders', toOrderNo: 'Order No.', toUser: 'User', toDue: 'Due (USDT)', toBase: 'Base amount', toChannelFee: 'Channel fee', toQuoteGross: 'Quote gross', toQuoteFee: 'Quote fee',
    toQuoteNet: 'Quote net', toAddress: 'Deposit address', toMode: 'Address mode', toMode_shared: 'Shared address', toMode_legacy: 'Legacy dedicated address', toSource: 'Channel', toSource_bot: 'Bot', toSource_web: 'Web',
    toCreated: 'Created at', toExpires: 'Expires at', toDepositHash: 'Deposit tx hash', toDeposit: 'Deposit record', toDepositNone: 'Not credited', toEvent: 'Chain receipt', toDetail: 'Top-up order',
    toRules: 'Bot / web top-up orders (read-only). Keyword accepts a UID (U100005), #order No., the amount due (e.g. 100.001), the deposit address, the deposit tx hash or an email prefix; exact UID or amount range filters are available as API parameters uid, amount_min, amount_max. Orders are only payment hints: crediting follows on-chain verification; handle short / over payments under Exceptions.',
  },
} as const;

const toText = (c: AdminCtx, k: string): string => str(c.t, k) || String(Reflect.get(I18N_TOPUP[c.lang], k) ?? k);
const whenS = (c: AdminCtx, iso: string | null | undefined): string => fmtDateTime(iso, { tz: c.s.sh.timezone || undefined, seconds: true });
const dash = (v: string | null | undefined): string => (v === null || v === undefined || v === '' ? '—' : v);
/** DECIMAL 文本去尾零（100.001000 → 100.001）；非数字原样。 */
export const trimAmount = (v: string | null | undefined): string => (v && /^-?[0-9]+(\.[0-9]+)?$/.test(v) ? v.replace(/(\.[0-9]*?)0+$/, '$1').replace(/\.$/, '') : dash(v));
const userLine = (c: AdminCtx, r: TopupOrderRow): string => `${r.uid} · ${r.email_masked || '—'}${r.telegram_username ? ' / @' + r.telegram_username : ''}`;

const STATUS_CHIP: Readonly<Record<string, ChipKind>> = { pending: 'amber', paid: 'green', expired: 'gray' };
const statusChip = (c: AdminCtx, s: string): CellVals => chip(STATUS_CHIP[s] ?? 'gray', statusText(c, 'order', s));

/** 充值订单抽屉：订单、报价、收款地址、到账与入账关联全部字段（只读）。 */
export function topupDrawer(c: AdminCtx, r: TopupOrderRow): void {
  const mode = toText(c, `toMode_${r.address_mode}`), source = toText(c, `toSource_${r.source}`);
  const fields = [
    { k: toText(c, 'toOrderNo'), v: `#${r.id}` },
    { k: str(c.t, 'cpStatus') || '状态', v: statusText(c, 'order', r.status) },
    { k: toText(c, 'toUser'), v: userLine(c, r), wide: true },
    { k: str(c.t, 'cpNetwork') || 'Network', v: r.network },
    { k: toText(c, 'toSource'), v: source },
    { k: toText(c, 'toDue'), v: trimAmount(r.amount_expected) },
    { k: toText(c, 'toBase'), v: trimAmount(r.base_amount) },
    { k: toText(c, 'toChannelFee'), v: trimAmount(r.channel_fee) },
    { k: toText(c, 'toQuoteGross'), v: trimAmount(r.quote_gross) },
    { k: toText(c, 'toQuoteFee'), v: trimAmount(r.quote_fee) },
    { k: toText(c, 'toQuoteNet'), v: trimAmount(r.quote_net) },
    { k: toText(c, 'toAddress'), v: r.address, wide: true },
    { k: toText(c, 'toMode'), v: r.registry_id ? `${mode} #${r.registry_id}` : mode },
    { k: toText(c, 'toCreated'), v: whenS(c, r.created_at) },
    { k: toText(c, 'toExpires'), v: whenS(c, r.expires_at) },
    { k: toText(c, 'toDeposit'), v: r.deposit_id ? `#${r.deposit_id} · ${trimAmount(r.deposit_amount)} USDT · ${dash(r.deposit_status)}` : toText(c, 'toDepositNone') },
    { k: toText(c, 'toDepositHash'), v: dash(r.deposit_tx_hash), wide: true },
    ...(r.payment_event_id ? [{ k: toText(c, 'toEvent'), v: `#${r.payment_event_id}` }] : []),
  ];
  openDrawer(c, { kind: 'crypto-topup', title: `${toText(c, 'toDetail')} #${r.id}`, subtitle: `${r.network} · ${statusText(c, 'order', r.status)}`, fields, json: JSON.stringify(r, null, 2), actions: [] });
}

/** 「充值订单」页签视图：keyword / 日期 / facet 网络·状态 / 应付与创建时间可排序 / CSV（另查 export 位）。 */
export const TOPUP_VIEW: ErasedView = defineView<TopupOrderRow>({
  key: 'topup-orders',
  source: { route: 'GET /api/admin/topup-orders', tabParam: 'none' },
  echo: '/admin/topup-orders',
  cols: [{ label: 'toOrderNo' }, { label: 'toUser' }, { label: 'cpNetwork', facet: 'network' }, { label: 'toDue', sortKey: 'amount_expected' }, { label: 'toQuoteNet' }, { label: 'toAddress' }, { label: 'cpStatus', facet: 'status', facetLabel: facetText('order') }, { label: 'toCreated', sortKey: 'created_at' }, { label: 'toDepositHash' }],
  cells: (c, r) => [
    cell(`#${r.id}`),
    cell(r.uid, r.email_masked ?? undefined),
    cell(r.network),
    cell(trimAmount(r.amount_expected)),
    cell(trimAmount(r.quote_net)),
    cell(r.address, undefined, true),
    statusChip(c, r.status),
    cell(whenS(c, r.created_at)),
    cell(dash(r.deposit_tx_hash), undefined, true),
  ],
  open: topupDrawer,
  rowActions: () => [{ label: 'cpDetail', run: topupDrawer }],
  csvName: 'topup-orders',
  note: (t) => str(t, 'toRules'),
});
